import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

function CheckIcon() {
	return (
		<svg width="14" height="14" viewBox="0 0 14 14" className="shrink-0 text-indigo-500">
			<path
				d="M3 7L6 10L11 4"
				stroke="currentColor"
				strokeWidth="1.5"
				fill="none"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

export type PricingTierProps = {
	name: string;
	price: string;
	period: string;
	description: string;
	features: string[];
	cta: string;
	highlighted?: boolean;
	delay?: string;
	href: string;
	isCurrent?: boolean;
	isUpgrade?: boolean;
	isDowngrade?: boolean;
	onSelect?: () => void;
};

export function PricingTier({
	name,
	price,
	period,
	description,
	features,
	cta,
	highlighted,
	delay,
	href,
	isCurrent,
	isUpgrade,
	isDowngrade,
	onSelect,
}: PricingTierProps) {
	const ctaLabel = isCurrent
		? "Current Plan"
		: isUpgrade
			? "Upgrade"
			: isDowngrade
				? "Downgrade"
				: cta;

	const handleClick = onSelect
		? (e: React.MouseEvent) => {
				e.preventDefault();
				onSelect();
			}
		: undefined;

	const emphasized = isCurrent || highlighted;

	return (
		<div
			className={`animate-fade-up flex flex-col rounded-xl border p-6 ${
				emphasized
					? "border-indigo-500/40 bg-card shadow-xl shadow-indigo-500/10"
					: "border-border bg-card"
			}`}
			style={delay ? { animationDelay: delay } : undefined}
		>
			{isCurrent ? (
				<Badge className="mb-4 w-fit bg-indigo-500/10 font-mono text-[10px] tracking-wider text-indigo-500 border-indigo-500/20">
					YOUR PLAN
				</Badge>
			) : highlighted ? (
				<Badge className="mb-4 w-fit bg-indigo-500 font-mono text-[10px] tracking-wider text-white">
					MOST POPULAR
				</Badge>
			) : (
				<div className="mb-4 h-5" />
			)}
			<h3 className="font-sans text-lg font-semibold text-foreground">{name}</h3>
			<div className="mt-2 flex items-baseline gap-1">
				<span className="font-sans text-4xl font-bold tracking-tight text-foreground">{price}</span>
				<span className="font-mono text-xs text-muted-foreground">{period}</span>
			</div>
			<p className="mt-3 text-sm leading-relaxed text-muted-foreground">{description}</p>

			<div className="mt-6 flex flex-col gap-3 border-t border-border pt-6">
				{features.map((f) => (
					<div key={f} className="flex items-center gap-2.5">
						<CheckIcon />
						<span className="text-sm text-foreground">{f}</span>
					</div>
				))}
			</div>

			<div className="mt-auto pt-6">
				{isCurrent ? (
					<Button
						disabled
						className="w-full font-mono text-xs tracking-wide bg-secondary text-muted-foreground"
					>
						{ctaLabel}
					</Button>
				) : (
					<a href={href} className="block" onClick={handleClick}>
						<Button
							className={`w-full font-mono text-xs tracking-wide ${
								highlighted || isUpgrade
									? "bg-indigo-500 text-white shadow-lg shadow-indigo-500/20 hover:bg-indigo-400"
									: "bg-secondary text-foreground hover:bg-secondary/80"
							}`}
						>
							{ctaLabel}
						</Button>
					</a>
				)}
			</div>
		</div>
	);
}
